import { useCallback, useEffect, useRef, useState } from "react";
import { FaEye, FaEyeSlash, FaLock, FaSignOutAlt, FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  changePasswordSessionRequest,
  finishPasskeyLogin,
  finishTotpLogin,
  loginRequest,
} from "../../../api/auth";
import { useAuth } from "../../../context/AuthContext";
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  passwordComplexityError,
  passwordRequirementsMessage,
} from "../../../constants/passwordPolicy";
import {
  homePath,
  loginPath,
  normalizeAlias,
  persistLastAlias,
  readLastAlias,
} from "../../../utils/contractPath";
import { normalizeNmLogin } from "../../../utils/normalizeNmLogin";
import { persistPasswordChangeToken } from "../../Login/Login";
import {
  TimedFlashPopup,
  TIMED_FLASH_DEFAULT_MS,
} from "../TimedFlashPopup";
import { TotpChallengeForm } from "../TotpChallenge/TotpChallengeForm";
import "../AppModals.css";
import "./UserSwitcher.css";

export function UserSwitcher({
  buttonRef = null,
  tabIndex = undefined,
  onButtonKeyDown = undefined,
} = {}) {
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [nmLogin, setNmLogin] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pwdSuccess, setPwdSuccess] = useState(null);
  const [totpUi, setTotpUi] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpInfo, setTotpInfo] = useState("");
  const [totpResolve, setTotpResolve] = useState(null);
  const rootRef = useRef(null);
  const loginInputRef = useRef(null);
  const currentPwdRef = useRef(null);
  const cancelLogoutRef = useRef(null);

  const label = user?.nm_user?.trim() || "—";
  const alias = normalizeAlias(user?.cd_alias) || readLastAlias();

  const clearTotpState = useCallback(() => {
    setTotpUi(null);
    setTotpCode("");
    setTotpInfo("");
    setTotpResolve(null);
  }, []);

  const closeModal = useCallback(() => {
    if (totpResolve?.reject) {
      totpResolve.reject(new Error("Código TOTP cancelado."));
    }
    clearTotpState();
    setModalOpen(false);
    setNmLogin("");
    setPassword("");
    setShowPassword(false);
    setError(null);
  }, [totpResolve, clearTotpState]);

  const closePwdModal = useCallback(() => {
    setPwdModalOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setError(null);
    setPwdSuccess(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!modalOpen || totpUi) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) closeModal();
    };
    window.addEventListener("keydown", onKey);
    window.requestAnimationFrame(() => loginInputRef.current?.focus());
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, loading, closeModal, totpUi]);

  useEffect(() => {
    if (!pwdModalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) closePwdModal();
    };
    window.addEventListener("keydown", onKey);
    if (!pwdSuccess) {
      window.requestAnimationFrame(() => currentPwdRef.current?.focus());
    }
    return () => window.removeEventListener("keydown", onKey);
  }, [pwdModalOpen, loading, pwdSuccess, closePwdModal]);

  useEffect(() => {
    if (!logoutConfirmOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLogoutConfirmOpen(false);
    };
    window.addEventListener("keydown", onKey);
    cancelLogoutRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [logoutConfirmOpen]);

  const openSwitchModal = () => {
    setOpen(false);
    setNmLogin("");
    setPassword("");
    setShowPassword(false);
    setError(null);
    clearTotpState();
    setModalOpen(true);
  };

  const openPasswordModal = () => {
    setOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setError(null);
    setPwdSuccess(null);
    setPwdModalOpen(true);
  };

  const openLogoutConfirm = () => {
    setOpen(false);
    setLogoutConfirmOpen(true);
  };

  const handleConfirmLogout = () => {
    setLogoutConfirmOpen(false);
    const sessionAlias =
      normalizeAlias(user?.cd_alias) || readLastAlias();
    logout();
    navigate(loginPath(sessionAlias) || "/", { replace: true });
  };

  const askTotpCode = useCallback((state) => {
    setLoading(false);
    setError(null);
    setTotpUi(state);
    setTotpCode("");
    setTotpInfo(
      state.mode === "enroll"
        ? "Leia o QR (ou introduza a chave) na app autenticadora e confirme o código."
        : "Introduza o código de 6 dígitos da app autenticadora."
    );
    return new Promise((resolve, reject) => {
      setTotpResolve({ resolve, reject });
    });
  }, []);

  const submitTotpCode = (event) => {
    event.preventDefault();
    const code = String(totpCode || "").trim();
    if (!/^\d{6}$/.test(code)) {
      setError("Introduza o código de 6 dígitos.");
      return;
    }
    setError(null);
    if (totpResolve?.resolve) {
      setLoading(true);
      const resolve = totpResolve.resolve;
      setTotpResolve(null);
      resolve(code);
    }
  };

  const cancelTotp = () => {
    if (totpResolve?.reject) {
      totpResolve.reject(new Error("Código TOTP cancelado."));
    }
    clearTotpState();
    setLoading(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    if (!alias) {
      setError(
        "Contrato da sessão não encontrado. Faça login pela URL do contrato."
      );
      return;
    }
    setLoading(true);
    try {
      const data = await loginRequest(
        normalizeNmLogin(nmLogin),
        password,
        alias
      );
      const resolvedAlias = normalizeAlias(data.cd_alias || alias);
      persistLastAlias(resolvedAlias);

      if (data.must_change_password) {
        persistPasswordChangeToken(data.password_change_token || "");
        logout();
        setModalOpen(false);
        clearTotpState();
        navigate(`/${resolvedAlias}/redefinir-senha`, {
          replace: true,
          state: {
            nm_login: data.nm_login || normalizeNmLogin(nmLogin),
            reason: "must_change",
          },
        });
        return;
      }

      let session = data;
      if (data.auth_step === "enroll" || data.auth_step === "passkey") {
        session = await finishPasskeyLogin(
          data.webauthn_pending_token,
          data.auth_step
        );
      } else if (
        data.auth_step === "enroll_totp" ||
        data.auth_step === "totp"
      ) {
        session = await finishTotpLogin(
          data.totp_pending_token,
          data.auth_step,
          askTotpCode
        );
      }

      login(session.access_token, {
        ...(session.user || data.user),
        cd_alias: resolvedAlias,
        active_company_id:
          session.active_company_id !== undefined
            ? session.active_company_id
            : null,
        primary_company_id:
          session.primary_company_id !== undefined
            ? session.primary_company_id
            : null,
        permitted_companies: session.permitted_companies ?? [],
        enabled_modules: session.enabled_modules ?? [],
        enabled_submodules: session.enabled_submodules ?? [],
        authorizations: session.authorizations ?? [],
      });
      // Remonta a app na home do contrato (menus/autorizações do novo utilizador).
      // Evita reload na rota actual, que podia cair no login com sessão inconsistente.
      window.location.assign(homePath(resolvedAlias));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao entrar.");
      clearTotpState();
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setPwdSuccess(null);
    const policyError = passwordComplexityError(newPassword);
    if (policyError) {
      setError(policyError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Senha e confirmação não coincidem.");
      return;
    }
    setLoading(true);
    try {
      await changePasswordSessionRequest({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });
      setError(null);
      setPwdSuccess("Senha alterada com sucesso!");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao alterar a senha.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="user-switcher" ref={rootRef}>
        <button
          ref={buttonRef}
          type="button"
          className="user-switcher__badge"
          aria-expanded={open}
          tabIndex={tabIndex}
          onKeyDown={onButtonKeyDown}
          onClick={() => setOpen((v) => !v)}
          title={user?.nm_login || "Conta do usuário"}
        >
          <span className="user-switcher__badge-text">{label}</span>
          <span className="user-switcher__chevron" aria-hidden>
            ▾
          </span>
        </button>
        {open ? (
          <ul className="user-switcher__menu" role="menu">
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="user-switcher__menu-item"
                onClick={openPasswordModal}
              >
                Trocar a Senha
              </button>
            </li>
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="user-switcher__menu-item"
                onClick={openSwitchModal}
              >
                Trocar de Usuário
              </button>
            </li>
            <li role="separator" className="user-switcher__menu-sep" />
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="user-switcher__menu-item user-switcher__menu-item--danger"
                onClick={openLogoutConfirm}
              >
                Sair do Sistema
              </button>
            </li>
          </ul>
        ) : null}
      </div>

      {modalOpen ? (
        <div
          className="usuarios-modal-overlay user-switcher-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !loading) closeModal();
          }}
        >
          {totpUi ? (
            <div
              className="totp-challenge-card"
              role="dialog"
              aria-modal="true"
              aria-label="Verificação em dois passos (TOTP)"
              onClick={(e) => e.stopPropagation()}
            >
              <TotpChallengeForm
                idPrefix="user-switcher-totp"
                mode={totpUi.mode}
                secret={totpUi.secret || ""}
                qrDataUrl={totpUi.qr_data_url || ""}
                otpauthUri={totpUi.otpauth_uri || ""}
                alias={alias || ""}
                info={totpInfo}
                error={error || ""}
                code={totpCode}
                onCodeChange={setTotpCode}
                onSubmit={submitTotpCode}
                onCancel={cancelTotp}
                loading={loading}
                showEnvBadge
              />
            </div>
          ) : (
            <div
              className="usuarios-modal usuarios-modal--confirm usuarios-modal--tipo-info"
              role="dialog"
              aria-modal="true"
              aria-labelledby="user-switcher-modal-titulo"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="usuarios-modal__accent" aria-hidden />
              <form
                className="user-switcher-modal__form"
                onSubmit={handleSubmit}
                noValidate
              >
                <div className="usuarios-modal__corpo">
                  <div className="usuarios-modal__icone" aria-hidden>
                    <FaUser />
                  </div>
                  <h3
                    id="user-switcher-modal-titulo"
                    className="usuarios-modal__titulo"
                  >
                    Trocar de Usuário
                  </h3>
                  {alias ? (
                    <p
                      className="user-switcher-modal__feedback user-switcher-modal__feedback--ok"
                      role="status"
                    >
                      Contrato: <strong>{alias}</strong>
                    </p>
                  ) : null}
                  {error ? (
                    <p
                      className="user-switcher-modal__feedback user-switcher-modal__feedback--error"
                      role="alert"
                    >
                      {error}
                    </p>
                  ) : null}
                  <div className="input-group">
                    <input
                      ref={loginInputRef}
                      type="text"
                      name="nm_login"
                      autoComplete="username"
                      placeholder="Login"
                      required
                      disabled={loading || !alias}
                      value={nmLogin}
                      onChange={(e) =>
                        setNmLogin(normalizeNmLogin(e.target.value))
                      }
                    />
                    <FaUser className="icon" />
                  </div>
                  <div className="input-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="has-toggle"
                      autoComplete="current-password"
                      placeholder="Senha"
                      required
                      disabled={loading || !alias}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <FaLock className="icon" />
                    <button
                      type="button"
                      className="icon-toggle"
                      tabIndex={0}
                      aria-label={
                        showPassword ? "Ocultar senha" : "Exibir senha"
                      }
                      disabled={loading || !alias}
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? (
                        <FaEyeSlash aria-hidden />
                      ) : (
                        <FaEye aria-hidden />
                      )}
                    </button>
                  </div>
                </div>
                <div className="usuarios-modal__rodape">
                  <button
                    type="button"
                    className="usuarios-btn-secondary"
                    disabled={loading}
                    onClick={closeModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="usuarios-btn-primary"
                    disabled={loading || !alias}
                  >
                    {loading ? "Entrando…" : "Entrar"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      ) : null}

      {pwdModalOpen ? (
        <div
          className="usuarios-modal-overlay user-switcher-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !loading) closePwdModal();
          }}
        >
          <div
            className="usuarios-modal usuarios-modal--confirm usuarios-modal--tipo-info user-switcher-modal--password"
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-password-modal-titulo"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="usuarios-modal__accent" aria-hidden />
            <form
              className="user-switcher-modal__form"
              onSubmit={handlePasswordSubmit}
              noValidate
              aria-hidden={pwdSuccess ? true : undefined}
            >
              <fieldset
                className="user-switcher-modal__fieldset"
                disabled={loading || Boolean(pwdSuccess)}
              >
              <div className="usuarios-modal__corpo">
                <div className="usuarios-modal__icone" aria-hidden>
                  <FaLock />
                </div>
                <h3
                  id="user-password-modal-titulo"
                  className="usuarios-modal__titulo"
                >
                  Trocar a Senha
                </h3>
                <p className="user-switcher-modal__hint" role="note">
                  {passwordRequirementsMessage()}
                </p>
                {error ? (
                  <p
                    className="user-switcher-modal__feedback user-switcher-modal__feedback--error"
                    role="alert"
                  >
                    {error}
                  </p>
                ) : null}
                <div className="input-group">
                  <input
                    ref={currentPwdRef}
                    type={showCurrent ? "text" : "password"}
                    name="current_password"
                    className="has-toggle"
                    autoComplete="current-password"
                    placeholder="Senha atual"
                    required
                    disabled={loading}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                  <FaLock className="icon" />
                  <button
                    type="button"
                    className="icon-toggle"
                    tabIndex={0}
                    aria-label={
                      showCurrent ? "Ocultar senha atual" : "Exibir senha atual"
                    }
                    disabled={loading}
                    onClick={() => setShowCurrent((v) => !v)}
                  >
                    {showCurrent ? (
                      <FaEyeSlash aria-hidden />
                    ) : (
                      <FaEye aria-hidden />
                    )}
                  </button>
                </div>
                <div className="input-group">
                  <input
                    type={showNew ? "text" : "password"}
                    name="new_password"
                    className="has-toggle"
                    autoComplete="new-password"
                    placeholder="Nova senha"
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    maxLength={PASSWORD_MAX_LENGTH}
                    disabled={loading}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <FaLock className="icon" />
                  <button
                    type="button"
                    className="icon-toggle"
                    tabIndex={0}
                    aria-label={
                      showNew ? "Ocultar nova senha" : "Exibir nova senha"
                    }
                    disabled={loading}
                    onClick={() => setShowNew((v) => !v)}
                  >
                    {showNew ? (
                      <FaEyeSlash aria-hidden />
                    ) : (
                      <FaEye aria-hidden />
                    )}
                  </button>
                </div>
                <div className="input-group">
                  <input
                    type={showConfirm ? "text" : "password"}
                    name="new_password_confirm"
                    className="has-toggle"
                    autoComplete="new-password"
                    placeholder="Confirmar nova senha"
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    maxLength={PASSWORD_MAX_LENGTH}
                    disabled={loading}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <FaLock className="icon" />
                  <button
                    type="button"
                    className="icon-toggle"
                    tabIndex={0}
                    aria-label={
                      showConfirm
                        ? "Ocultar confirmação"
                        : "Exibir confirmação"
                    }
                    disabled={loading}
                    onClick={() => setShowConfirm((v) => !v)}
                  >
                    {showConfirm ? (
                      <FaEyeSlash aria-hidden />
                    ) : (
                      <FaEye aria-hidden />
                    )}
                  </button>
                </div>
              </div>
              <div className="usuarios-modal__rodape">
                <button
                  type="button"
                  className="usuarios-btn-secondary"
                  disabled={loading}
                  onClick={closePwdModal}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="usuarios-btn-primary"
                  disabled={loading}
                >
                  {loading ? "Gravando…" : "Alterar Senha"}
                </button>
              </div>
              </fieldset>
            </form>
            {pwdSuccess ? (
              <TimedFlashPopup
                tone="ok"
                contained
                showClose
                durationMs={TIMED_FLASH_DEFAULT_MS}
                onDismiss={closePwdModal}
              >
                {pwdSuccess}
              </TimedFlashPopup>
            ) : null}
          </div>
        </div>
      ) : null}

      {logoutConfirmOpen ? (
        <div
          className="usuarios-modal-overlay"
          role="presentation"
          onMouseDown={(e) => e.preventDefault()}
        >
          <div
            className="usuarios-modal usuarios-modal--confirm usuarios-modal--tipo-alerta"
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-switcher-logout-confirm-titulo"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="usuarios-modal__accent" aria-hidden />
            <div className="usuarios-modal__corpo">
              <div className="usuarios-modal__icone" aria-hidden>
                <FaSignOutAlt />
              </div>
              <h3
                id="user-switcher-logout-confirm-titulo"
                className="usuarios-modal__titulo"
              >
                Sair do Sistema?
              </h3>
              <div className="usuarios-modal__texto">
                <p className="usuarios-modal__texto--simples">
                  Deseja mesmo encerrar a sessão?
                </p>
              </div>
            </div>
            <div className="usuarios-modal__rodape">
              <button
                ref={cancelLogoutRef}
                type="button"
                className="usuarios-btn-secondary"
                onClick={() => setLogoutConfirmOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="usuarios-btn-primary usuarios-modal-btn--perigo"
                onClick={handleConfirmLogout}
              >
                Sair do Sistema
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
